/* eslint-disable sort-keys */
import React, {
  createContext,
  useCallback,
  useRef,
  useEffect,
  useState,
  useContext,
} from 'react';
import { useLocation } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';

export type RouteTimerObj = {
  id: string;
  pageLoadStartTime: number;
  pathname: string;
  stopDuration?: (vital: RouteTimerObj) => void;
  type: 'initial' | 'route_change';
};

type PageLoadMetricsContextType = {
  routeTimer?: RouteTimerObj;
};

const pageLoadMetricsContext = createContext<
  PageLoadMetricsContextType | undefined
>(undefined);
pageLoadMetricsContext.displayName = 'PageLoadMetricsContext';

export function usePageLoadMetricsContext() {
  const pageLoadMetrics = useContext(pageLoadMetricsContext);
  if (!pageLoadMetrics) {
    throw new Error(
      'pageLoadMetricsContext value is undefined. Make sure you use the PageLoadMetricsProvider before using the context.'
    );
  }
  return pageLoadMetrics;
}

export type PageLoadMetricsProviderProps = {
  children?: React.ReactNode;
};

export function PageLoadMetricsProvider(
  props: Readonly<PageLoadMetricsProviderProps>
) {
  const { pathname } = useLocation();

  const [routeTimer, setRouteTimer] = useState<RouteTimerObj | undefined>();

  const visitedPathnames = useRef<{ [pathname: string]: boolean }>({});
  const stoppedTimers = useRef<{ [id: string]: boolean }>({});
  const switchedTabTracker = useRef<{ [id: string]: boolean }>({});

  const getCurrentRouteTimerId = useCallback(
    () => routeTimer?.id,
    [routeTimer?.id]
  );
  const getSwitchedTabsStatus = useCallback(
    (id: string) => switchedTabTracker.current[id],
    []
  );

  useEffect(() => {
    const startTime = performance.now();
    const isVisited = visitedPathnames.current[pathname];
    const reference = datadogRum.startDurationVital('IngramPageLoadVital', {
      context: {
        type: isVisited ? 'route_change' : 'initial',
        switchedTabs: false,
      },
    });
    const id = crypto.randomUUID();

    if (routeTimer?.pathname !== pathname) {
      setRouteTimer({
        id,
        pageLoadStartTime: startTime,
        pathname,
        type: isVisited ? 'route_change' : 'initial',
        stopDuration: (vital) => {
          try {
            if (stoppedTimers.current[id]) return;

            stoppedTimers.current[id] = true;
            datadogRum.stopDurationVital(reference, {
              context: {
                switchedTabs: getSwitchedTabsStatus(vital.id),
              },
            });
          } catch (error) {
            console.error('Error posting to Datadog:', error);
          }
        },
      });
    }

    visitedPathnames.current[pathname] = true;
    stoppedTimers.current[id] = false;
  }, [getSwitchedTabsStatus, pathname, routeTimer?.pathname]);

  useEffect(() => {
    const handleTabChange = () => {
      const timerId = getCurrentRouteTimerId();
      if (document.visibilityState === 'hidden' && timerId) {
        switchedTabTracker.current[timerId] = true;
      }
    };

    window.addEventListener('visibilitychange', handleTabChange);
    return () => {
      window.removeEventListener('visibilitychange', handleTabChange);
    };
  }, [getCurrentRouteTimerId]);

  const { Provider } = pageLoadMetricsContext;

  return (
    <Provider
      value={{
        routeTimer,
      }}
    >
      {props.children}
    </Provider>
  );
}
